import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ConverterVideoLoadError, loadConverterVideoFile, type LoadedVideo} from './loadConverterVideoFile'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {reportError} from '../../../common/errors/reportError'
import {trackEvent} from '../../../common/analytics/trackEvent'

export interface UseConverterVideoUploadResult {
  busy: boolean
  error: string | null
  handleFileSelected: (file: File) => Promise<void>
}

/** Shared validate/load/error/busy logic for picking a video — used by both the
 * source panel's dropzone and the filmstrip preview's own drop target. */
export const useConverterVideoUpload = (onVideoLoaded: (video: LoadedVideo) => void): UseConverterVideoUploadResult => {
  const {t} = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelected = useCallback(
    async (file: File) => {
      setError(null)
      setBusy(true)
      addBreadcrumb('video-to-gif', 'source-picked', 'info', {size: file.size, type: file.type})
      trackEvent('video_to_gif_upload', {format: file.type, size_bytes: file.size})
      try {
        const loaded = await loadConverterVideoFile(file)
        addBreadcrumb('video-to-gif', 'source-loaded', 'info', {
          duration: loaded.duration,
          w: loaded.width,
          h: loaded.height,
        })
        onVideoLoaded(loaded)
      } catch (err) {
        if (err instanceof ConverterVideoLoadError) {
          setError(t(`videoToGif.upload.${err.key}`))
        } else {
          reportError(err, 'video-to-gif-load', {size: file.size, type: file.type})
          setError(t('videoToGif.upload.failed'))
        }
      } finally {
        setBusy(false)
      }
    },
    [onVideoLoaded, t],
  )

  return {busy, error, handleFileSelected}
}
